'use client';

export interface EngagementTier {
  name: string;
  priceRange: string;
  period: string;
  description: string;
  idealFor: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
}

interface EngagementTierCardProps {
  tier: EngagementTier;
  onSelect: (tierName: string) => void;
}

export default function EngagementTierCard({ tier, onSelect }: EngagementTierCardProps) {
  return (
    <div
      className={`relative rounded-xl p-8 ${
        tier.highlighted
          ? 'bg-gradient-to-br from-accent-muted to-surface border-2 border-accent scale-105 shadow-2xl'
          : 'bg-light-surface dark:bg-surface border border-light-border dark:border-border'
      }`}
    >
      {/* Badge */}
      {tier.badge && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-accent text-accent-foreground px-4 py-1 rounded-full text-sm font-bold">
            {tier.badge}
          </span>
        </div>
      )}

      {/* Tier Name */}
      <h3 className="text-2xl font-bold text-light-text dark:text-foreground mb-2">{tier.name}</h3>

      {/* Price Range */}
      <div className="mb-4">
        <span className="text-4xl font-bold text-accent">{tier.priceRange}</span>
        <span className="text-light-text-secondary dark:text-foreground-tertiary ml-2">/ {tier.period}</span>
      </div>

      {/* Description */}
      <p className="text-light-text-secondary dark:text-foreground-secondary mb-4 min-h-[3rem]">
        {tier.description}
      </p>

      {/* Ideal For */}
      <div className="mb-6 p-3 bg-accent-muted dark:bg-accent-muted rounded-lg border border-border">
        <p className="text-sm">
          <span className="font-semibold text-light-text dark:text-foreground">Ideal for: </span>
          <span className="text-light-text-secondary dark:text-foreground-secondary">{tier.idealFor}</span>
        </p>
      </div>

      {/* CTA Button */}
      <button
        onClick={() => onSelect(tier.name.toLowerCase().replace(/\s+/g, '-'))}
        className={`w-full py-3 rounded-lg font-semibold transition-all mb-8 cursor-pointer ${
          tier.highlighted
            ? 'bg-accent text-accent-foreground hover:bg-accent-hover shadow-lg hover:shadow-xl'
            : 'bg-light-surface dark:bg-background border border-border text-accent hover:border-accent hover:bg-accent-muted'
        }`}
      >
        Start a Conversation
      </button>

      {/* Features List */}
      <ul className="space-y-3">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <span className="text-green-500 dark:text-green-400 mt-0.5">&#10003;</span>
            <span className="text-light-text dark:text-foreground text-sm">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
